import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import type { TootHeadingAlignment } from "../../../../modules/layout/components/TootHeading/TootHeading.props";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

const Link = resolveTootPrimitive("Link");

const renderStars = (rating: number) => {
  const filledStars = Math.round(rating);

  return (
    <span aria-hidden="true" className="tracking-[0.08em] text-[var(--color-toot-amber)]">
      {"★".repeat(filledStars)}
      <span className="text-toot-grid-strong">{"★".repeat(5 - filledStars)}</span>
    </span>
  );
};

export const FeaturedTestimonialBlock = (props: IFeaturedTestimonialBlockProps) => {
  const testimonial = props.testimonial;

  if (!testimonial?.quote) {
    return null;
  }

  const { avatar, company, link: sourceLink, personName, quote, rating, role } = testimonial;
  const actions = props.buttons ?? [];
  const visibleSourceLink =
    sourceLink && !actions.some((action) => action.href === sourceLink.href)
      ? sourceLink
      : undefined;
  const alignment: TootHeadingAlignment = "left";
  const identity = [role, company].filter(Boolean).join(" · ");
  const hasIntro = Boolean(props.eyebrow || props.heading || props.subheading);
  const hasMedia = Boolean(avatar?.defaultSrc);

  return (
    <TootSectionShell
      className="relative"
      containerClassName="relative"
      id={props.anchorId ?? undefined}
      rootClassName={props.parentClass}
      spacing={props.spacing ?? "loose"}
      tone="dark"
      width="wide"
    >
      <article className="toot-heat-panel p-[clamp(1.25rem,4vw,3rem)] text-[var(--color-toot-foreground)]">
        <div
          className={cn(
            "relative z-10 grid gap-8 lg:gap-12",
            hasMedia && "lg:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)] lg:items-start",
          )}
        >
          <div className="flex min-w-0 flex-col">
            {hasIntro ? (
              <TootHeading
                alignment={alignment}
                className="max-w-5xl [&_.toot-subheading]:max-w-2xl"
                eyebrow={props.eyebrow}
                heading={props.heading}
                subheading={props.subheading}
              />
            ) : null}

            <figure className={cn("max-w-4xl", hasIntro && "mt-8 md:mt-10")}>
              {rating != null ? (
                <div
                  aria-label={`${rating}/5 verified rating`}
                  className="mb-5 flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2 text-[0.68rem] font-black uppercase tracking-[0.18em] text-[color-mix(in_oklab,var(--color-toot-muted)_86%,var(--color-toot-foreground))]"
                >
                  {renderStars(rating)}
                  <span>{rating}/5</span>
                </div>
              ) : null}

              <blockquote className="m-0 min-w-0 [overflow-wrap:anywhere]">
                <p className="m-0 text-pretty text-[1.55rem] md:text-[2.75rem] font-black leading-[1.05] tracking-[-0.04em] text-toot-foreground">
                  “{quote}”
                </p>
              </blockquote>

              <figcaption className="mt-7 flex min-w-0 flex-col gap-5 border-t border-toot-grid/90 pt-6 md:mt-8">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="min-w-0">
                    {personName ? (
                      <cite className="block break-words not-italic text-base font-black tracking-[-0.02em] text-toot-foreground md:text-lg">
                        {personName}
                      </cite>
                    ) : null}
                    {identity ? (
                      <div className="mt-1 break-words text-[0.72rem] font-bold uppercase leading-5 tracking-[0.14em] text-[var(--color-toot-muted)]">
                        {identity}
                      </div>
                    ) : null}
                  </div>
                </div>

                {visibleSourceLink?.href || actions.length ? (
                  <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                      {visibleSourceLink?.href ? (
                        <Link
                          aria-label={visibleSourceLink.ariaLabel ?? undefined}
                          className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_42%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_8%,transparent)] px-3.5 py-2 text-[0.68rem] font-black uppercase tracking-[0.14em] text-[var(--color-toot-orange)] transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--color-toot-red)] hover:bg-[var(--color-toot-red)] hover:text-[var(--color-primary-foreground)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                          href={visibleSourceLink.href}
                          rel={
                            visibleSourceLink.target === "_blank"
                              ? "noopener noreferrer"
                              : undefined
                          }
                          target={visibleSourceLink.target ?? undefined}
                        >
                          <span className="min-w-0 [overflow-wrap:anywhere]">
                            {visibleSourceLink.text}
                          </span>
                          <span aria-hidden="true">→</span>
                        </Link>
                      ) : null}
                      <TootActions actions={actions} alignment={alignment} />
                    </div>
                  </div>
                ) : null}
              </figcaption>
            </figure>
          </div>

          {hasMedia ? (
            <aside className="flex min-w-0 flex-col gap-5 lg:pt-2" aria-label="Testimonial media">
              <TootMediaFrame
                alt={avatar?.alternateText ?? ""}
                aspectRatio="1 / 1"
                className="toot-media-heat rounded-[var(--radius-toot-frame)] border-[color-mix(in_oklab,var(--color-toot-orange)_34%,transparent)]"
                focalPoint={avatar?.focalPoint ?? undefined}
                height={avatar?.height ?? undefined}
                sizes={avatar?.sizes ?? undefined}
                src={avatar?.defaultSrc}
                srcSet={avatar?.srcSet ?? undefined}
                width={avatar?.width ?? undefined}
              />
            </aside>
          ) : null}
        </div>
      </article>
    </TootSectionShell>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
